import { API_ENDPOINTS, PAGE_SIZE, PATIENT_API_ENDPOINTS } from "../config";
import { http } from "../utils/http";
import { store } from "../State/store/configureStore";
import { ROLE_PATIENT } from "../security/roles";
import fileService from "./fileService";
import { showProgress, closeProgress } from "../State/actions/uploadProgress";
import { dashboardMock } from "../utils/dashboardMock";
import { dashboardPeriodFilter } from "../utils/utils";

class SurveysService {
  add(survey) {
    return http.request(API_ENDPOINTS.addSurvey(), "post", survey);
  }

  edit(surveyId, survey) {
    return http.request(API_ENDPOINTS.loadSurvey(surveyId), "patch", survey);
  }
  importSurveyResponse(
    survey,
    csvFile,
    multiGroupColumn,
    multiGroupValueColumn
  ) {
    let formData = new FormData();
    formData.append("files", csvFile);
    if (multiGroupColumn && multiGroupValueColumn) {
      formData.append("multiGroupColumn", multiGroupColumn);
      formData.append("multiGroupValueColumn", multiGroupValueColumn);
    }
    let api = API_ENDPOINTS.patientSurveyImportFromCSV(survey);
    return http
      .request(
        API_ENDPOINTS.patientSurveyImportFromCSV(survey),
        "post",
        formData,
        null,
        (progressEvent) => {
          const uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          store.dispatch(showProgress(uploadProgress));
        },
        120000
      )
      .then((response) => {
        store.dispatch(closeProgress());
        let strings = [];
        if (response._embedded && response._embedded.strings) {
          strings = response._embedded.strings || [];
        }
        return strings;
      })
      .catch((e) => {
        store.dispatch(closeProgress());
      });
  }
  load(page, all, active, patientTypeId) {
    let pageSize = PAGE_SIZE;
    if (all) {
      pageSize = 1000;
    }

    let endpoint =
      API_ENDPOINTS.loadSurveys() + `?page=${page}&size=${pageSize}`;
    if (!active) {
      endpoint += `&allStatus=True`;
    }

    if (patientTypeId) {
      endpoint += `&pTypeId=${patientTypeId}`;
    }

    return http.request(endpoint).then((data) => {
      if (data && data._embedded) {
        return {
          data: page <= data.page.totalPages ? data._embedded.surveys : [],
          loadMore: data.page.totalPages > page + 1,
        };
      } else {
        return {
          data: [],
          loadMore: false,
        };
      }
    });
  }

  loadSingle(surveyId, patientId) {
    let api = API_ENDPOINTS.loadSurvey(surveyId);
    if (patientId) {
      api += `?patientId=${patientId}`;
    }
    return http.request(api);
  }

  loadPatientSurveys(patientId) {
    return http
      .request(API_ENDPOINTS.loadPatientSurveys(patientId))
      .then((data) => {
        return data && data._embedded ? data._embedded.surveyResponses : [];
      });
  }

  deleteSurvey(surveyId) {
    return http.request(API_ENDPOINTS.loadSurvey(surveyId), "delete");
  }

  submitAnswers(messageId, responseData, patientId) {
    let role = store.getState().user.role;
    let api = messageId
      ? API_ENDPOINTS.surveyResponse(patientId, messageId)
      : API_ENDPOINTS.directSurveyResponse(patientId);
    if (role === ROLE_PATIENT) {
      if (messageId) {
        api = PATIENT_API_ENDPOINTS.surveyResponse(messageId);
      } else {
        api = PATIENT_API_ENDPOINTS.surveyResponsesAdHoc;
      }
    }
    return http.request(api, "post", responseData);
  }

  submitExternalAnswers(responseData, patientId){
    let api = API_ENDPOINTS.externalSurveyResponse(patientId);
    return http.request(api, "post", responseData)
  }

  fetchDetailedReportUrl(patientId, startDate, endDate) {
    let api = API_ENDPOINTS.detailedAssessmentReport(patientId) + "?";
    if (startDate) {
      api += `&startDate=${startDate}`;
    }
    if (endDate) {
      api += `&endDate=${endDate}`;
    }
    return api;
  }

  fetchTherapyComplianceReportUrl(patientId, endDate) {
    let api = API_ENDPOINTS.therapyComplianceReport(patientId);
    if (endDate) {
      api += "?endDate=" + endDate;
    }
    return api;
    // return http.request(api);
  }

  async fetchPatientReportData(patientId, filter = {}) {
    let api = API_ENDPOINTS.patientCustomReport(patientId) + "?";

    api = dashboardPeriodFilter(api, filter);

    return http.request(api);
    // return dashboardMock;
  }

  async fetchPatientReportDataTab(patientId, tabId, filter) {
    let api = API_ENDPOINTS.patientCustomReportTab(patientId, tabId);

    api = dashboardPeriodFilter(api, filter);

    return http.request(api);
  }

  fetchAnonymousPatientReportData = (filter = {}) => {
    let { patientTypeId, fromDate, toDate } = filter;
    let api = API_ENDPOINTS.patientCustomReportAnonymous(patientTypeId);
    if (fromDate) {
      api += `&fromDate=${new Date(fromDate).getTime()}`;
    }
    if (toDate) {
      api += `&toDate=${new Date(toDate).getTime()}`;
    }

    return http.request(api);
  };

  loadPendingSurveysForPatient(patientId) {
    return http
      .request(API_ENDPOINTS.provisionedSurveys(patientId))
      .then((data) => {
        return data && data._embedded ? data._embedded.provisionedSurveys : [];
      });
  }

  loadPatientSubmittedSurveys(patientId) {
    return http
      .request(API_ENDPOINTS.patientSubmittedSurveys(patientId))
      .then((data) => {
        return data
          ? {
              availableSurveys: data.availableSurveys,
              latestSurveyId: data.latestSurveyId,
            } || {}
          : {};
      });
  }

  loadSubmittedSurveyResponsesBySurveyId(
    patientId,
    surveyId,
    page = 0,
    size = 30
  ) {
    return http
      .request(
        API_ENDPOINTS.patientSurveysResponsesById(
          patientId,
          surveyId,
          page,
          size
        )
      )
      .then((data) => {
        return {
          responses:
            data && data._embedded ? data._embedded.surveyResponses : [],
          totalPages: data.page.totalPages,
        };
      });
  }

  deleteSurveyResponse(patientId, responseId) {
    return http.request(
      API_ENDPOINTS.surveyResponses(patientId, responseId),
      "delete"
    );
  }

  exportSurvey(surveyId) {
    return http.request(API_ENDPOINTS.surveyExport(surveyId));
  }

  downloadSurveyResponsesAsCSV(patientId, surveyId) {
    return fileService.getFile(
      API_ENDPOINTS.surveyResponsesCSV(patientId, surveyId)
    );
  }

  fetchAGP(patientId, start, end) {
    return http
      .request(API_ENDPOINTS.fetchAGPData(patientId, start, end))
  }

  loadSurveyByName(name) {
    return http
      .request(API_ENDPOINTS.getSurveyByName(name))
  }

  addWithAI(file, patientTypeId) {
    const formData = new FormData();
    formData.append("file", file);
    return http.request(API_ENDPOINTS.addSurveyWithAI(patientTypeId), "post", formData);
  }
}

const surveysService = new SurveysService();

export default surveysService;
